import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme, RADIUS, TYPE } from '../theme';
import { Card, EmptyState, Loader, Segmented } from '../components/ui';
import { huntApi, heatColor, type KillBucket, type KillStats } from '../huntEconomy';

// ─────────────────────────────────────────────────────────────────
// P6 · Statistik över fällt vilt: per art, säsong, månad, såt, jägare (och mark i profilen) plus
// karta med värmekarta. Källor och dubblettregler: apps/api/src/services/killStats.ts.
// ─────────────────────────────────────────────────────────────────

function Bars({ title, buckets, testId }: { title: string; buckets: KillBucket[]; testId?: string }) {
  const T = useTheme();
  if (!buckets.length) return null;
  const max = Math.max(...buckets.map(b => b.count), 1);
  return (
    <Card data-testid={testId} style={{ display: 'grid', gap: 8, minWidth: 0 }}>
      <div style={{ ...TYPE.label, color: T.textSub }}>{title}</div>
      {buckets.map(b => (
        <div key={b.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(70px,38%) 1fr auto', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.label}>{b.label}</span>
          <span aria-hidden style={{ height: 12, borderRadius: RADIUS.pill, background: T.surface2, overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${(b.count / max) * 100}%`, background: T.accentFill, borderRadius: RADIUS.pill }} />
          </span>
          <span style={{ fontWeight: 700, minWidth: 24, textAlign: 'right' }}>{b.count}</span>
        </div>
      ))}
    </Card>
  );
}

/** Kartan: värmekarta (rutor) eller punkter per djur. */
function KillMap({ stats, center }: { stats: KillStats; center?: [number, number] | null }) {
  const T = useTheme();
  const div = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const [mode, setMode] = useState<'heat' | 'points'>('heat');

  useEffect(() => {
    if (!div.current || map.current) return;
    const m = L.map(div.current, { zoomControl: true, attributionControl: true }).setView(center ?? [62, 16], center ? 13 : 5);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap', maxZoom: 19 }).addTo(m);
    map.current = m;
    layer.current = L.layerGroup().addTo(m);
    return () => { m.remove(); map.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const m = map.current, g = layer.current;
    if (!m || !g) return;
    g.clearLayers();
    const bounds: L.LatLngExpression[] = [];
    if (mode === 'heat') {
      const max = Math.max(1, ...stats.heat.map(c => c.count));
      const dLat = stats.cellMeters / 111_320;
      for (const c of stats.heat) {
        const dLng = stats.cellMeters / (111_320 * Math.cos((c.lat * Math.PI) / 180));
        const b: L.LatLngBoundsExpression = [[c.lat - dLat / 2, c.lng - dLng / 2], [c.lat + dLat / 2, c.lng + dLng / 2]];
        L.rectangle(b, { color: heatColor(c.count, max), weight: 1, fillColor: heatColor(c.count, max), fillOpacity: 0.55 })
          .bindTooltip(`${c.count} djur`).addTo(g);
        bounds.push([c.lat, c.lng]);
      }
    } else {
      for (const p of stats.points) {
        L.circleMarker([p.lat, p.lng], { radius: 6 + Math.min(6, p.antal - 1), color: '#fff', weight: 2, fillColor: '#b05d0a', fillOpacity: 0.9 })
          .bindTooltip(`${p.antal > 1 ? `${p.antal} × ` : ''}${p.viltslag} · ${p.date}`).addTo(g);
        bounds.push([p.lat, p.lng]);
      }
    }
    if (bounds.length) m.fitBounds(L.latLngBounds(bounds as L.LatLngTuple[]).pad(0.3), { maxZoom: 15 });
    setTimeout(() => m.invalidateSize(), 50);
  }, [stats, mode]);

  return (
    <Card data-testid="kill-map" style={{ display: 'grid', gap: 10, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ ...TYPE.label, color: T.textSub }}>Var viltet fälldes ({stats.points.reduce((n, p) => n + p.antal, 0)} med position)</div>
        <Segmented label="Kartvy" size="sm" block={false} value={mode} onChange={setMode} options={[{ value: 'heat', label: 'Värmekarta' }, { value: 'points', label: 'Punkter' }]} />
      </div>
      <div ref={div} style={{ height: 320, borderRadius: RADIUS.md, overflow: 'hidden', background: T.map }} />
      {mode === 'heat' && <div style={{ fontSize: 12, color: T.textMuted }}>Rutor om {stats.cellMeters} m – mörkare = fler fällda.</div>}
    </Card>
  );
}

/** Statistikvyn – på marken och i jägarprofilen. */
export function KillStatsView({ load, scope, center }: {
  load: (season: string | null) => Promise<KillStats>;
  scope: 'mark' | 'profil';
  center?: [number, number] | null;
}) {
  const T = useTheme();
  const [season, setSeason] = useState<string | null>(null);
  const [stats, setStats] = useState<KillStats | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    load(season).then(s => { if (live) { setStats(s); setErr(''); } }).catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [season]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div role="alert" style={{ color: T.danger }}>{err}</div>;
  if (!stats) return <Loader text="Räknar…" />;
  if (!stats.total && !stats.seasons.length) {
    return <EmptyState title="Inget fällt vilt än" sub={scope === 'mark' ? 'Statistiken byggs av fällt vilt som bekräftats efter jakt, viltregistreringar och nålar på kartan.' : 'När du anger fällt vilt efter en jakt i Qvenari syns det här.'} />;
  }
  return (
    <div data-testid="kill-stats" style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
          Säsong
          <select aria-label="Säsong" value={season ?? 'alla'} onChange={e => setSeason(e.target.value === 'alla' ? null : e.target.value)}
            style={{ minHeight: 44, padding: '0 10px', borderRadius: RADIUS.sm, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontFamily: 'inherit', fontSize: 15 }}>
            <option value="alla">Alla säsonger</option>
            {stats.seasons.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <span style={{ fontSize: 28, fontWeight: 800 }} data-testid="kill-total">{stats.total}</span>
        <span style={{ color: T.textSub }}>fällda djur</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))', gap: 14 }}>
        <Bars title="Per viltslag" buckets={stats.bySpecies} testId="stats-species" />
        {!season && stats.bySeason.length > 1 && <Bars title="Per säsong" buckets={stats.bySeason} />}
        <Bars title="Per månad (jaktåret)" buckets={stats.byMonth} />
        {scope === 'mark' && <Bars title="Per såt" buckets={stats.bySat} testId="stats-sat" />}
        {scope === 'mark' && <Bars title="Per jägare" buckets={stats.byHunter} testId="stats-hunter" />}
        {scope === 'profil' && stats.byMark.length > 0 && <Bars title="Per jaktmark" buckets={stats.byMark} />}
      </div>
      {stats.points.length > 0 && <KillMap stats={stats} center={center} />}
    </div>
  );
}

export function StatistikTab({ markId, center }: { markId: string; center?: [number, number] | null }) {
  return (
    <div style={{ padding: '0 clamp(10px,3vw,28px) 32px', maxWidth: 1100 }}>
      <h2 style={{ ...TYPE.section, margin: '8px 0 12px' }}>Statistik</h2>
      <KillStatsView scope="mark" center={center} load={season => huntApi.markKillStats(markId, season)} />
    </div>
  );
}

/** Jägarprofilen: egen statistik från fällt vilt i Qvenari (alla marker). */
export function MyKillStats() {
  const T = useTheme();
  return (
    <Card style={{ marginBottom: 20, display: 'grid', gap: 10 }}>
      <div>
        <div style={{ color: T.text, fontSize: 15, fontWeight: 700 }}>Fällt i Qvenari</div>
        <div style={{ color: T.textSub, fontSize: 13 }}>Räknas automatiskt från ditt bekräftade fällda vilt på alla marker. Bara du ser din statistik här.</div>
      </div>
      <KillStatsView scope="profil" load={season => huntApi.myKillStats(season)} />
    </Card>
  );
}
